import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { scenariosApi } from '@/utils/api';
import type {
  SupplyDemandAnalysis,
  TeamCapacityResponse,
  OptimizationResult,
  OptimizationConstraints,
  EpicPriority,
  AllocationSuggestion,
  ScenarioComparison,
  BudgetAnalysisResult,
  RecommendationsResult,
  VirtualTeam,
} from '@/types';

export interface Scenario {
  id: string;
  name: string;
  description?: string;
  planningPeriodId: string;
  isBaseline: boolean;
  createdById?: string;
  createdAt: string;
  updatedAt: string;
  planningPeriod?: {
    id: string;
    name: string;
  };
  createdBy?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  _count?: {
    allocations: number;
    featureAllocations: number;
  };
}

interface ScenariosState {
  scenarios: Scenario[];
  currentScenario: Scenario | null;
  isLoading: boolean;
  error: string | null;
  comparisonData: any | null;

  // Optimization state
  supplyDemand: SupplyDemandAnalysis | null;
  supplyDemandLoading: boolean;
  supplyDemandError: string | null;

  teamCapacity: TeamCapacityResponse | null;
  teamCapacityLoading: boolean;
  teamCapacityError: string | null;

  optimizationResult: OptimizationResult | null;
  optimizationLoading: boolean;
  optimizationError: string | null;

  detailedComparison: ScenarioComparison | null;
  detailedComparisonLoading: boolean;
  detailedComparisonError: string | null;

  applySuggestionsLoading: boolean;
  applySuggestionsError: string | null;

  // Budget analysis state
  budgetAnalysis: BudgetAnalysisResult | null;
  budgetAnalysisLoading: boolean;
  budgetAnalysisError: string | null;

  // Recommendations state
  recommendations: RecommendationsResult | null;
  recommendationsLoading: boolean;
  recommendationsError: string | null;

  // Virtual teams state
  virtualTeams: VirtualTeam[];
  virtualTeamsLoading: boolean;
  virtualTeamsError: string | null;
}

const initialState: ScenariosState = {
  scenarios: [],
  currentScenario: null,
  isLoading: false,
  error: null,
  comparisonData: null,

  // Optimization state
  supplyDemand: null,
  supplyDemandLoading: false,
  supplyDemandError: null,

  teamCapacity: null,
  teamCapacityLoading: false,
  teamCapacityError: null,

  optimizationResult: null,
  optimizationLoading: false,
  optimizationError: null,

  detailedComparison: null,
  detailedComparisonLoading: false,
  detailedComparisonError: null,

  applySuggestionsLoading: false,
  applySuggestionsError: null,

  // Budget analysis state
  budgetAnalysis: null,
  budgetAnalysisLoading: false,
  budgetAnalysisError: null,

  // Recommendations state
  recommendations: null,
  recommendationsLoading: false,
  recommendationsError: null,

  // Virtual teams state
  virtualTeams: [],
  virtualTeamsLoading: false,
  virtualTeamsError: null,
};

// Async thunks
export const fetchScenarios = createAsyncThunk(
  'scenarios/fetchScenarios',
  async (params: { planningPeriodId?: string; createdById?: string } = {}) => {
    const response = await scenariosApi.getAll(params);
    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to fetch scenarios');
    }
    return response.data;
  }
);

export const fetchScenarioById = createAsyncThunk(
  'scenarios/fetchScenarioById',
  async (id: string) => {
    const response = await scenariosApi.getById(id);
    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to fetch scenario');
    }
    return response.data;
  }
);

export const createScenario = createAsyncThunk(
  'scenarios/createScenario',
  async (data: Partial<Scenario>) => {
    const response = await scenariosApi.create(data);
    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to create scenario');
    }
    return response.data;
  }
);

export const updateScenario = createAsyncThunk(
  'scenarios/updateScenario',
  async ({ id, data }: { id: string; data: Partial<Scenario> }) => {
    const response = await scenariosApi.update(id, data);
    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to update scenario');
    }
    return response.data;
  }
);

export const cloneScenario = createAsyncThunk(
  'scenarios/cloneScenario',
  async ({ id, data }: { id: string; data: { name: string; description?: string } }) => {
    const response = await scenariosApi.clone(id, data);
    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to clone scenario');
    }
    return response.data;
  }
);

export const compareScenarios = createAsyncThunk(
  'scenarios/compareScenarios',
  async (scenarioIds: string[]) => {
    const response = await scenariosApi.compare(scenarioIds);
    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to compare scenarios');
    }
    return response.data;
  }
);

export const setBaselineScenario = createAsyncThunk(
  'scenarios/setBaselineScenario',
  async (id: string) => {
    const response = await scenariosApi.setBaseline(id);
    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to set baseline scenario');
    }
    return response.data;
  }
);

export const deleteScenario = createAsyncThunk(
  'scenarios/deleteScenario',
  async (id: string) => {
    const response = await scenariosApi.delete(id);
    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to delete scenario');
    }
    return id;
  }
);

// Optimization thunks
export const fetchSupplyDemand = createAsyncThunk(
  'scenarios/fetchSupplyDemand',
  async ({ scenarioId, programId }: { scenarioId: string; programId?: string }) => {
    const response = await scenariosApi.getSupplyDemand(scenarioId, programId);
    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to fetch supply/demand analysis');
    }
    return response.data as SupplyDemandAnalysis;
  }
);

export const fetchTeamCapacity = createAsyncThunk(
  'scenarios/fetchTeamCapacity',
  async ({ scenarioId, programId }: { scenarioId: string; programId?: string }) => {
    const response = await scenariosApi.getTeamCapacity(scenarioId, programId);
    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to fetch team capacity');
    }
    return response.data as TeamCapacityResponse;
  }
);

export const runOptimization = createAsyncThunk(
  'scenarios/runOptimization',
  async ({ scenarioId, constraints, priorities }: {
    scenarioId: string;
    constraints?: Partial<OptimizationConstraints>;
    priorities?: EpicPriority[];
  }) => {
    const response = await scenariosApi.optimize(scenarioId, {
      constraints,
      priorities,
    });
    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to run optimization');
    }
    return (response.data as any).result as OptimizationResult;
  }
);

export const applyOptimizationSuggestions = createAsyncThunk(
  'scenarios/applyOptimizationSuggestions',
  async ({ scenarioId, suggestions }: {
    scenarioId: string;
    suggestions: AllocationSuggestion[];
  }) => {
    const response = await scenariosApi.applySuggestions(scenarioId, suggestions);
    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to apply suggestions');
    }
    return response.data;
  }
);

// Budget analysis thunks
export const fetchBudgetAnalysis = createAsyncThunk(
  'scenarios/fetchBudgetAnalysis',
  async ({ scenarioId, programId, fiscalYear }: { scenarioId: string; programId: string; fiscalYear?: number }) => {
    const response = await scenariosApi.getBudgetAnalysis(scenarioId, programId, fiscalYear);
    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to fetch budget analysis');
    }
    return response.data as BudgetAnalysisResult;
  }
);

export const updateTeamAdjustment = createAsyncThunk(
  'scenarios/updateTeamAdjustment',
  async ({ scenarioId, teamId, adjustedMemberCount, isReleased, adjustedCostProfileId, notes }: {
    scenarioId: string;
    teamId: string;
    adjustedMemberCount: number;
    isReleased?: boolean;
    adjustedCostProfileId?: string;
    notes?: string;
  }) => {
    const response = await scenariosApi.upsertTeamAdjustment(scenarioId, teamId, {
      adjustedMemberCount,
      isReleased,
      adjustedCostProfileId,
      notes,
    });
    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to update team adjustment');
    }
    return response.data;
  }
);

export const removeTeamAdjustment = createAsyncThunk(
  'scenarios/removeTeamAdjustment',
  async ({ scenarioId, teamId }: { scenarioId: string; teamId: string }) => {
    const response = await scenariosApi.deleteTeamAdjustment(scenarioId, teamId);
    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to remove team adjustment');
    }
    return { scenarioId, teamId };
  }
);

export const updateEpicEstimate = createAsyncThunk(
  'scenarios/updateEpicEstimate',
  async ({ scenarioId, epicId, adjustedTeamWeeks, notes }: {
    scenarioId: string;
    epicId: string;
    adjustedTeamWeeks: number;
    notes?: string;
  }) => {
    const response = await scenariosApi.upsertEpicEstimate(scenarioId, epicId, { adjustedTeamWeeks, notes });
    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to update epic estimate');
    }
    return response.data;
  }
);

export const removeEpicEstimate = createAsyncThunk(
  'scenarios/removeEpicEstimate',
  async ({ scenarioId, epicId }: { scenarioId: string; epicId: string }) => {
    const response = await scenariosApi.deleteEpicEstimate(scenarioId, epicId);
    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to remove epic estimate');
    }
    return { scenarioId, epicId };
  }
);

export const fetchDetailedComparison = createAsyncThunk(
  'scenarios/fetchDetailedComparison',
  async ({ scenarioId, otherScenarioId }: { scenarioId: string; otherScenarioId: string }) => {
    const response = await scenariosApi.compareDetailed(scenarioId, otherScenarioId);
    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to compare scenarios');
    }
    return response.data as ScenarioComparison;
  }
);

export const fetchRecommendations = createAsyncThunk(
  'scenarios/fetchRecommendations',
  async ({ scenarioId, programId }: { scenarioId: string; programId?: string }) => {
    const response = await scenariosApi.getRecommendations(scenarioId, programId);
    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to fetch recommendations');
    }
    return response.data as RecommendationsResult;
  }
);

// Virtual team thunks
export const fetchVirtualTeams = createAsyncThunk(
  'scenarios/fetchVirtualTeams',
  async ({ scenarioId, programId }: { scenarioId: string; programId?: string }) => {
    const response = await scenariosApi.getVirtualTeams(scenarioId, programId);
    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to fetch virtual teams');
    }
    return ((response.data as any)?.virtualTeams || response.data || []) as VirtualTeam[];
  }
);

export const createVirtualTeam = createAsyncThunk(
  'scenarios/createVirtualTeam',
  async ({ scenarioId, programId, name, costProfileId, memberCount, notes }: {
    scenarioId: string;
    programId: string;
    name: string;
    costProfileId: string;
    memberCount: number;
    notes?: string;
  }) => {
    const response = await scenariosApi.createVirtualTeam(scenarioId, { programId, name, costProfileId, memberCount, notes });
    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to create virtual team');
    }
    return response.data as VirtualTeam;
  }
);

export const deleteVirtualTeam = createAsyncThunk(
  'scenarios/deleteVirtualTeam',
  async ({ scenarioId, virtualTeamId }: { scenarioId: string; virtualTeamId: string }) => {
    const response = await scenariosApi.deleteVirtualTeam(scenarioId, virtualTeamId);
    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to delete virtual team');
    }
    return virtualTeamId;
  }
);

export const createVirtualTeamAllocation = createAsyncThunk(
  'scenarios/createVirtualTeamAllocation',
  async ({ scenarioId, virtualTeamId, epicId, teamWeeks, notes }: {
    scenarioId: string;
    virtualTeamId: string;
    epicId: string;
    teamWeeks: number;
    notes?: string;
  }) => {
    const response = await scenariosApi.createVirtualTeamAllocation(scenarioId, virtualTeamId, { epicId, teamWeeks, notes });
    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to create virtual team allocation');
    }
    return response.data;
  }
);

export const deleteVirtualTeamAllocation = createAsyncThunk(
  'scenarios/deleteVirtualTeamAllocation',
  async ({ scenarioId, virtualTeamId, allocationId }: {
    scenarioId: string;
    virtualTeamId: string;
    allocationId: string;
  }) => {
    const response = await scenariosApi.deleteVirtualTeamAllocation(scenarioId, virtualTeamId, allocationId);
    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to delete virtual team allocation');
    }
    return { virtualTeamId, allocationId };
  }
);

const scenariosSlice = createSlice({
  name: 'scenarios',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setCurrentScenario: (state, action: PayloadAction<Scenario | null>) => {
      state.currentScenario = action.payload;
    },
    clearComparisonData: (state) => {
      state.comparisonData = null;
    },
    clearOptimizationResult: (state) => {
      state.optimizationResult = null;
      state.optimizationError = null;
    },
    clearSupplyDemand: (state) => {
      state.supplyDemand = null;
      state.supplyDemandError = null;
    },
    clearTeamCapacity: (state) => {
      state.teamCapacity = null;
      state.teamCapacityError = null;
    },
    clearDetailedComparison: (state) => {
      state.detailedComparison = null;
      state.detailedComparisonError = null;
    },
    clearBudgetAnalysis: (state) => {
      state.budgetAnalysis = null;
      state.budgetAnalysisError = null;
    },
    clearRecommendations: (state) => {
      state.recommendations = null;
      state.recommendationsError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Scenarios
      .addCase(fetchScenarios.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchScenarios.fulfilled, (state, action) => {
        state.isLoading = false;
        const payload = action.payload as any;
        state.scenarios = payload.scenarios || payload;
      })
      .addCase(fetchScenarios.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to fetch scenarios';
      })

      // Fetch Scenario by ID
      .addCase(fetchScenarioById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchScenarioById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentScenario = action.payload as Scenario;
      })
      .addCase(fetchScenarioById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to fetch scenario';
      })

      // Create Scenario
      .addCase(createScenario.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createScenario.fulfilled, (state, action) => {
        state.isLoading = false;
        state.scenarios.unshift(action.payload as Scenario);
      })
      .addCase(createScenario.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to create scenario';
      })

      // Update Scenario
      .addCase(updateScenario.pending, (state) => {
        state.error = null;
      })
      .addCase(updateScenario.fulfilled, (state, action) => {
        const payload = action.payload as Scenario;
        const index = state.scenarios.findIndex(scenario => scenario.id === payload.id);
        if (index !== -1) {
          state.scenarios[index] = payload;
        }
        if (state.currentScenario?.id === payload.id) {
          state.currentScenario = payload;
        }
      })
      .addCase(updateScenario.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to update scenario';
      })

      // Clone Scenario
      .addCase(cloneScenario.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(cloneScenario.fulfilled, (state, action) => {
        state.isLoading = false;
        state.scenarios.unshift(action.payload as Scenario);
      })
      .addCase(cloneScenario.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to clone scenario';
      })

      // Compare Scenarios
      .addCase(compareScenarios.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(compareScenarios.fulfilled, (state, action) => {
        state.isLoading = false;
        state.comparisonData = action.payload;
      })
      .addCase(compareScenarios.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to compare scenarios';
      })

      // Set Baseline Scenario
      .addCase(setBaselineScenario.pending, (state) => {
        state.error = null;
      })
      .addCase(setBaselineScenario.fulfilled, (state, action) => {
        const updatedScenarios = action.payload as Scenario[];
        // Update all scenarios to reflect new baseline
        updatedScenarios.forEach(updatedScenario => {
          const index = state.scenarios.findIndex(scenario => scenario.id === updatedScenario.id);
          if (index !== -1) {
            state.scenarios[index] = updatedScenario;
          }
        });
      })
      .addCase(setBaselineScenario.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to set baseline scenario';
      })

      // Delete Scenario
      .addCase(deleteScenario.pending, (state) => {
        state.error = null;
      })
      .addCase(deleteScenario.fulfilled, (state, action) => {
        const deletedId = action.payload as string;
        state.scenarios = state.scenarios.filter(scenario => scenario.id !== deletedId);
        if (state.currentScenario?.id === deletedId) {
          state.currentScenario = null;
        }
      })
      .addCase(deleteScenario.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to delete scenario';
      })

      // Fetch Supply/Demand
      .addCase(fetchSupplyDemand.pending, (state) => {
        state.supplyDemandLoading = true;
        state.supplyDemandError = null;
      })
      .addCase(fetchSupplyDemand.fulfilled, (state, action) => {
        state.supplyDemandLoading = false;
        state.supplyDemand = action.payload;
      })
      .addCase(fetchSupplyDemand.rejected, (state, action) => {
        state.supplyDemandLoading = false;
        state.supplyDemandError = action.error.message || 'Failed to fetch supply/demand analysis';
      })

      // Fetch Team Capacity
      .addCase(fetchTeamCapacity.pending, (state) => {
        state.teamCapacityLoading = true;
        state.teamCapacityError = null;
      })
      .addCase(fetchTeamCapacity.fulfilled, (state, action) => {
        state.teamCapacityLoading = false;
        state.teamCapacity = action.payload;
      })
      .addCase(fetchTeamCapacity.rejected, (state, action) => {
        state.teamCapacityLoading = false;
        state.teamCapacityError = action.error.message || 'Failed to fetch team capacity';
      })

      // Run Optimization
      .addCase(runOptimization.pending, (state) => {
        state.optimizationLoading = true;
        state.optimizationError = null;
      })
      .addCase(runOptimization.fulfilled, (state, action) => {
        state.optimizationLoading = false;
        state.optimizationResult = action.payload;
      })
      .addCase(runOptimization.rejected, (state, action) => {
        state.optimizationLoading = false;
        state.optimizationError = action.error.message || 'Failed to run optimization';
      })

      // Apply Optimization Suggestions
      .addCase(applyOptimizationSuggestions.pending, (state) => {
        state.applySuggestionsLoading = true;
        state.applySuggestionsError = null;
      })
      .addCase(applyOptimizationSuggestions.fulfilled, (state) => {
        state.applySuggestionsLoading = false;
        // Clear optimization result after applying
        state.optimizationResult = null;
      })
      .addCase(applyOptimizationSuggestions.rejected, (state, action) => {
        state.applySuggestionsLoading = false;
        state.applySuggestionsError = action.error.message || 'Failed to apply suggestions';
      })

      // Fetch Detailed Comparison
      .addCase(fetchDetailedComparison.pending, (state) => {
        state.detailedComparisonLoading = true;
        state.detailedComparisonError = null;
      })
      .addCase(fetchDetailedComparison.fulfilled, (state, action) => {
        state.detailedComparisonLoading = false;
        state.detailedComparison = action.payload;
      })
      .addCase(fetchDetailedComparison.rejected, (state, action) => {
        state.detailedComparisonLoading = false;
        state.detailedComparisonError = action.error.message || 'Failed to compare scenarios';
      })

      // Fetch Budget Analysis
      .addCase(fetchBudgetAnalysis.pending, (state) => {
        state.budgetAnalysisLoading = true;
        state.budgetAnalysisError = null;
      })
      .addCase(fetchBudgetAnalysis.fulfilled, (state, action) => {
        state.budgetAnalysisLoading = false;
        state.budgetAnalysis = action.payload;
      })
      .addCase(fetchBudgetAnalysis.rejected, (state, action) => {
        state.budgetAnalysisLoading = false;
        state.budgetAnalysisError = action.error.message || 'Failed to fetch budget analysis';
      })

      // Update Team Adjustment (refetch budget analysis after)
      .addCase(updateTeamAdjustment.rejected, (state, action) => {
        state.budgetAnalysisError = action.error.message || 'Failed to update team adjustment';
      })

      // Remove Team Adjustment
      .addCase(removeTeamAdjustment.rejected, (state, action) => {
        state.budgetAnalysisError = action.error.message || 'Failed to remove team adjustment';
      })

      // Update Epic Estimate
      .addCase(updateEpicEstimate.rejected, (state, action) => {
        state.budgetAnalysisError = action.error.message || 'Failed to update epic estimate';
      })

      // Remove Epic Estimate
      .addCase(removeEpicEstimate.rejected, (state, action) => {
        state.budgetAnalysisError = action.error.message || 'Failed to remove epic estimate';
      })

      // Fetch Recommendations
      .addCase(fetchRecommendations.pending, (state) => {
        state.recommendationsLoading = true;
        state.recommendationsError = null;
      })
      .addCase(fetchRecommendations.fulfilled, (state, action) => {
        state.recommendationsLoading = false;
        state.recommendations = action.payload;
      })
      .addCase(fetchRecommendations.rejected, (state, action) => {
        state.recommendationsLoading = false;
        state.recommendationsError = action.error.message || 'Failed to fetch recommendations';
      })

      // Fetch Virtual Teams
      .addCase(fetchVirtualTeams.pending, (state) => {
        state.virtualTeamsLoading = true;
        state.virtualTeamsError = null;
      })
      .addCase(fetchVirtualTeams.fulfilled, (state, action) => {
        state.virtualTeamsLoading = false;
        state.virtualTeams = action.payload;
      })
      .addCase(fetchVirtualTeams.rejected, (state, action) => {
        state.virtualTeamsLoading = false;
        state.virtualTeamsError = action.error.message || 'Failed to fetch virtual teams';
      })

      // Create Virtual Team
      .addCase(createVirtualTeam.pending, (state) => {
        state.virtualTeamsLoading = true;
      })
      .addCase(createVirtualTeam.fulfilled, (state, action) => {
        state.virtualTeamsLoading = false;
        state.virtualTeams.push(action.payload);
      })
      .addCase(createVirtualTeam.rejected, (state, action) => {
        state.virtualTeamsLoading = false;
        state.virtualTeamsError = action.error.message || 'Failed to create virtual team';
      })

      // Delete Virtual Team
      .addCase(deleteVirtualTeam.fulfilled, (state, action) => {
        state.virtualTeams = state.virtualTeams.filter(vt => vt.id !== action.payload);
      })
      .addCase(deleteVirtualTeam.rejected, (state, action) => {
        state.virtualTeamsError = action.error.message || 'Failed to delete virtual team';
      })

      // Create/Delete Virtual Team Allocation
      .addCase(createVirtualTeamAllocation.rejected, (state, action) => {
        state.virtualTeamsError = action.error.message || 'Failed to create allocation';
      })
      .addCase(deleteVirtualTeamAllocation.rejected, (state, action) => {
        state.virtualTeamsError = action.error.message || 'Failed to delete allocation';
      });
  },
});

export const {
  clearError,
  setCurrentScenario,
  clearComparisonData,
  clearOptimizationResult,
  clearSupplyDemand,
  clearTeamCapacity,
  clearDetailedComparison,
  clearBudgetAnalysis,
  clearRecommendations,
} = scenariosSlice.actions;

export default scenariosSlice.reducer;
